import React, { useState, useMemo } from 'react';
import { FormMetadata, FormVersion } from '@/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/src/components/ui/card";
import { Input } from "@/src/components/ui/input";
import { Label } from "@/src/components/ui/label";
import { Button } from "@/src/components/ui/button";
import { Badge } from "@/src/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Switch } from "@/src/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { 
  Info, AlertTriangle, CheckCircle2, FolderTree, CornerDownRight, X,
  Database, Plus, Hash, AlignLeft, List, Box, AlertCircle, History,
  Calculator, MoveRight, Trash2, Search, ShieldCheck, Play,
  Upload, Download, Pencil, Layers
} from 'lucide-react';
import { processContainerForRow, ContainerDef } from './engine';
import AliasBuilder, { GlobalAlias } from '../AliasBuilder';

type VarType = 'number' | 'enum' | 'text' | 'container' | 'meta';

interface Variable {
  id: string;
  name: string;
  type: VarType;
  isOrphan?: boolean;
  desc?: string;
  returnType?: string;
  formName?: string;
  formId?: string;
}

type RuleCondition = { type: 'condition', id: string, var: Variable, operator: string, value: string };
type RuleGroup = { type: 'group', id: string, operator: 'AND'|'OR', children: (RuleCondition | RuleGroup)[] };



export interface ParameterConstructorProps {
  forms: FormMetadata[];
  versions: FormVersion[];
}

export default function ParameterConstructor({ forms, versions }: ParameterConstructorProps) {
  const [paramName, setParamName] = useState("Consumo Médio (KM/L)");
  const [targetForm, setTargetForm] = useState("global");
  const [scope, setScope] = useState<"individual" | "geral">("individual");
  const [returnType, setReturnType] = useState("number");
  const [fallback, setFallback] = useState("");
  
  const [ruleRoot, setRuleRoot] = useState<RuleGroup>({ type: 'group', id: 'root', operator: 'AND', children: [] });
  
  const [draggedVar, setDraggedVar] = useState<Variable | null>(null);

  const [globalAliases, setGlobalAliases] = useState<GlobalAlias[]>(() => {
    try {
       const str = localStorage.getItem('insights-global-aliases');
       if (str) return JSON.parse(str);
    } catch {}
    return [];
  });

  const [globalInputs, setGlobalInputs] = useState<{ id: string, name: string, type: string, value: any }[]>(() => {
    try {
       const str = localStorage.getItem('insights-global-inputs');
       if (str) return JSON.parse(str);
    } catch {}
    return [];
  });

  const [aliasBuilderOpen, setAliasBuilderOpen] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('insights-global-aliases', JSON.stringify(globalAliases));
  }, [globalAliases]);

  React.useEffect(() => {
    localStorage.setItem('insights-global-inputs', JSON.stringify(globalInputs));
  }, [globalInputs]);

  const PARAM_LOCAL_STORAGE_KEY = '@forms-asa-branca/custom-parameters';

  const [savedParams, setSavedParams] = useState<ContainerDef[]>(() => {
    try {
      const existingStr = localStorage.getItem(PARAM_LOCAL_STORAGE_KEY);
      if (existingStr) return JSON.parse(existingStr);
    } catch (e) {}

    // Attempt fallback to old key just in case to migrate
    try {
      const oldStr = localStorage.getItem('asa_branca_custom_parameters');
      if (oldStr) {
        localStorage.setItem(PARAM_LOCAL_STORAGE_KEY, oldStr);
        return JSON.parse(oldStr);
      }
    } catch(e) {}

    return [];
  });

  // CACHE_RESET_PARAMETERS_V4: Clear cache on first load after deployment to prevent legacy form crashes
  React.useEffect(() => {
    try {
      if (!localStorage.getItem('asa_branca_cache_cleared_v4')) {
        localStorage.removeItem(PARAM_LOCAL_STORAGE_KEY);
        localStorage.setItem('asa_branca_cache_cleared_v4', 'true');
        setSavedParams([]);
      }
    } catch (e) {}
  }, []);

  const ALL_VARIABLES = useMemo(() => {
    const vars: Variable[] = [];

    // Global Metadatas
    vars.push({ id: 'meta_lat_long', formId: 'global', formName: 'Metadados Globais', name: 'Lat/Long', type: 'meta', desc: 'Coordenadas GPS do dispositivo' });
    vars.push({ id: 'meta_created_at', formId: 'global', formName: 'Metadados Globais', name: 'Data de Envio', type: 'meta', desc: 'ISO 8601 Database Timestamp' });

    if (!forms || !versions) return vars;

    const byKey = new Map<string, any>();
    versions.forEach(fv => {
      const formId = (fv as any).formId || (fv as any).form_id;
      const form = forms.find(f => f.id === formId);
      const vNum = typeof fv.version === 'number' ? fv.version : 0;

      (fv.schema || []).forEach((field: any) => {
        const key = `${formId}::${field.id}`;
        const prev = byKey.get(key);
        if (!prev || vNum > prev._version) {
          byKey.set(key, {
            formId,
            formTitle: form?.title || 'Formulário Desconhecido',
            fieldId: field.id,
            fieldLabel: field.label,
            fieldType: field.type,
            options: field.options,
            _version: vNum
          });
        }
      });
    });

    Array.from(byKey.values()).forEach(field => {
      let vType: VarType = 'text';
      if (['number', 'rating'].includes(field.fieldType)) vType = 'number';
      if (['select', 'radio'].includes(field.fieldType)) vType = 'enum';

      vars.push({
        id: `${field.formId}::${field.fieldId}`,
        formId: field.formId,
        formName: field.formTitle,
        name: field.fieldLabel,
        type: vType,
        desc: `Tipo: ${field.fieldType}`,
      });
    });

    // Global Aliases
    globalAliases.forEach(alias => {
        if (alias.linkedFieldIds && alias.linkedFieldIds.length > 0) {
            let varType: VarType = 'text';
            const mappedField = Array.from(byKey.values()).find(f => f.fieldId === alias.linkedFieldIds[0]);
            if (mappedField) {
                if (['number', 'rating'].includes(mappedField.fieldType)) varType = 'number';
                if (['select', 'radio'].includes(mappedField.fieldType)) varType = 'enum';
            }
            vars.push({
                id: alias.id,
                formId: 'global',
                formName: 'Agrupador Global',
                name: `[Global] ${alias.name}`,
                type: varType,
                desc: 'Variável consolidada de múltiplos formulários'
            });
        }
    });

    // Global Inputs
    globalInputs.forEach(input => {
      let vType: VarType = 'text';
      if (input.type === 'number') vType = 'number';
      if (input.type === 'enumlist') vType = 'enum';
      // Treat time as text to allow passing into HOUR_TO_TIME if needed, or numeric?
      // Since it's a raw value, we will treat it as 'text' by default unless it's a number.
      
      vars.push({
        id: `GLOBAL::${input.id}`,
        formId: 'global',
        formName: 'Variáveis Globais',
        name: `[Input] ${input.name}`,
        type: vType,
        desc: `Valor Padrão: ${input.value}`
      });
    });

    // Custom Parameters (Containers)
    savedParams.forEach(param => {
      vars.push({
        id: param.id,
        formId: 'global',
        formName: 'Metadados Globais',
        name: `[Fórmula] ${param.name}`,
        type: 'container',
        desc: `Cálculo: ${param.name}`,
        returnType: param.returnValue?.type === 'CONSTANT' || param.returnValue?.type === 'FIELD' ? 'number' : 'number' // Can improve later
      });
    });

    return vars;
  }, [forms, versions, globalAliases, globalInputs, savedParams]);

  const [inspectedVar, setInspectedVar] = useState<Variable | null>(null);

  const handleEquationDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedEqIndex !== null) {
      if (draggedEqIndex === targetIndex) {
        setDraggedEqIndex(null);
        return;
      }
      const newEls = [...equationElements];
      const [moved] = newEls.splice(draggedEqIndex, 1);
      newEls.splice(targetIndex, 0, moved);
      setEquationElements(newEls);
      setDraggedEqIndex(null);
    } else if (draggedVar && draggedVar.type === 'number') {
      const newEls = [...equationElements];
      newEls.splice(targetIndex, 0, draggedVar);
      setEquationElements(newEls);
      setDraggedVar(null);
    }
  };

  const [equationElements, setEquationElements] = useState<(string | Variable)[]>([]);
  const [draggedEqIndex, setDraggedEqIndex] = useState<number | null>(null);

  const [pendingScope, setPendingScope] = useState<"individual"|"geral" | null>(null);

  // AST Parser Functions
  // Shunting Yard simplificado para parsing Client-Side para AST (Issue 18)
  const parseEquationToAST = (elements: (string | Variable)[]): any => {
    if (elements.length === 0) return { type: "CONSTANT", value: 0 };
    
    const precedence: Record<string, number> = {
      'OR': 1, 'AND': 2,
      '=': 3, '<>': 3, '>': 3, '<': 3, '>=': 3, '<=': 3,
      '+': 4, '-': 4,
      '×': 5, '÷': 5
    };
    const output: any[] = [];
    const operators: string[] = [];
    const functionArgCount: number[] = [];

    const getOpName = (sym: string) => {
      if (sym === '+') return 'ADD';
      if (sym === '-') return 'SUBTRACT';
      if (sym === '×') return 'MULTIPLY';
      if (sym === '÷') return 'DIVIDE';
      if (sym === '=') return 'EQUALS';
      if (sym === '<>') return 'NOT_EQUALS';
      if (sym === '>') return 'GREATER_THAN';
      if (sym === '<') return 'LESS_THAN';
      if (sym === '>=') return 'GREATER_THAN_EQUALS';
      if (sym === '<=') return 'LESS_THAN_EQUALS';
      if (sym === 'AND') return 'AND';
      if (sym === 'OR') return 'OR';
      return sym;
    };

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (typeof el === 'object') {
         output.push({ type: 'FIELD', value: el.id }); // Using real element id
      } else if (typeof el === 'string' && !isNaN(Number(el)) && el.trim() !== '') {
         output.push({ type: 'CONSTANT', value: Number(el) });
      } else if (typeof el === 'string' && ((el.startsWith("'") && el.endsWith("'")) || (el.startsWith('"') && el.endsWith('"')) || (el.startsWith('`') && el.endsWith('`')))) {
         output.push({ type: 'CONSTANT', value: el.slice(1, -1) });
      } else if (typeof el === 'string' && el.endsWith('(') && el.length > 1) {
         operators.push(el);
         functionArgCount.push(1); // At least one arg
      } else if (el === '(') {
         operators.push(el);
      } else if (el === ';') {
         while (operators.length && operators[operators.length - 1] !== '(' && !operators[operators.length - 1].endsWith('(')) {
           const op = operators.pop()!;
           const right = output.pop();
           const left = output.pop();
           if(left && right && op) output.push({ type: 'OPERATION', operation: getOpName(op), left, right });
         }
         if (functionArgCount.length > 0) {
            functionArgCount[functionArgCount.length - 1]++;
         }
      } else if (el === ')') {
         while (operators.length && operators[operators.length - 1] !== '(' && !operators[operators.length - 1].endsWith('(')) {
           const op = operators.pop()!;
           const right = output.pop();
           const left = output.pop();
           if(left && right && op) output.push({ type: 'OPERATION', operation: getOpName(op), left, right });
         }
         const openOp = operators.pop(); 
         if (openOp && openOp.endsWith('(') && openOp !== '(') {
             const argCount = functionArgCount.pop() || 1;
             const args = [];
             for (let a = 0; a < argCount; a++) {
                 const arg = output.pop();
                 if (arg) args.unshift(arg);
             }

             if (openOp === 'THIS_ROW(') {
                 // THIS_ROW takes exactly one field arg
                 output.push({ type: 'THIS_ROW', value: args[0]?.value });
             } else {
                 output.push({ type: 'FUNCTION', value: openOp.replace('(', ''), args: args });
             }
         }
      } else if (precedence[el]) {
         while (operators.length && precedence[operators[operators.length - 1]] >= precedence[el]) {
             const op = operators.pop()!;
             const right = output.pop();
             const left = output.pop();
             if(left && right && op) output.push({ type: 'OPERATION', operation: getOpName(op), left, right });
         }
         operators.push(el);
      }
    }
    
    while (operators.length) {
       const op = operators.pop()!;
       const right = output.pop();
       const left = output.pop();
       if(left && right && op) output.push({ type: 'OPERATION', operation: getOpName(op), left, right });
    }

    return output[0] || { type: "CONSTANT", value: 0 };
  };

  const [showASTDialog, setShowASTDialog] = useState(false);
  const [compiledAST, setCompiledAST] = useState<any>(null);

  // Sandbox State
  const [showSandbox, setShowSandbox] = useState(false);
  const [sandboxRow, setSandboxRow] = useState<Record<string, string>>({});
  const [sandboxResult, setSandboxResult] = useState<{ internalResult: any, finalResult: any } | null>(null);
  
  // Realtime Live Tester flag
  const [liveTestMode, setLiveTestMode] = useState(false);

  // Engine Compiler (Extracting payload generation)
  const generateASTPayload = React.useCallback((): ContainerDef => {
    const mapVisualToEngineRules = (vGroup: RuleGroup): any => {
      const group: any = { operator: vGroup.operator, conditions: [] };
      for (const child of vGroup.children) {
         if (child.type === 'group') {
            group.conditions.push({ nested: mapVisualToEngineRules(child) });
         } else if (child.type === 'condition') {
            group.conditions.push({ fieldId: child.var.id, operator: child.operator, value: child.value });
         }
      }
      return group;
    };

    return {
      id: PARAM_CURRENT_ID, 
      name: paramName,
      premises: (ruleRoot.children.length > 0) ? mapVisualToEngineRules(ruleRoot) : { operator: 'AND', conditions: [] },
      returnValue: equationElements.length > 0
        ? parseEquationToAST(equationElements) 
        : { type: "CONSTANT", value: fallback },
      fallbackValue: fallback === "" ? null : (returnType === 'number' && !isNaN(Number(fallback)) ? Number(fallback) : fallback),
      //@ts-ignore metadata is just for us
      _metadata: {
        targetForm, scope, returnType
      }
    };
  }, [paramName, ruleRoot, equationElements, fallback, returnType, targetForm, scope]);



  const [viewMode, setViewMode] = useState<'editor' | 'list'>('editor');


  const parseCalculationToString = (calc: any): string => {
    if (!calc) return '';
    if (calc.type === 'CONSTANT') {
        if (typeof calc.value === 'string') return `"${calc.value}"`;
        return String(calc.value);
    }
    if (calc.type === 'FIELD') {
      const v = ALL_VARIABLES.find(x => x.id === calc.value);
      return v ? `[${v.name}]` : `[${calc.value}]`;
    }
    if (calc.type === 'FUNCTION') {
      return `${calc.value}(${calc.args ? calc.args.map((a:any) => parseCalculationToString(a)).join(', ') : ''})`;
    }
    if (calc.type === 'OPERATION') {
      const ops: Record<string, string> = { ADD: '+', SUBTRACT: '-', MULTIPLY: '*', DIVIDE: '/' };
      return `(${parseCalculationToString(calc.left)} ${ops[calc.operation] || '?'} ${parseCalculationToString(calc.right)})`;
    }
    return '';
  };

  const handleSaveAndCompile = () => {
    const ast = generateASTPayload();
    setCompiledAST(ast);
    setShowASTDialog(true);

    // Salvar no localStorage
    try {
      const existingStr = localStorage.getItem(PARAM_LOCAL_STORAGE_KEY);
      let existingParams: ContainerDef[] = [];
      if (existingStr) {
        existingParams = JSON.parse(existingStr);
      }

      // Atualizar ou adicionar novo parâmetro (usamos paramName como chave/id simples por agora,
      // mas idealmente usaríamos um ID único gerado)
      const paramId = `param_${paramName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      ast.id = paramId;

      const idx = existingParams.findIndex(p => p.name === paramName || p.id === paramId);
      if (idx !== -1) {
        existingParams[idx] = ast;
      } else {
        existingParams.push(ast);
      }

      localStorage.setItem(PARAM_LOCAL_STORAGE_KEY, JSON.stringify(existingParams));
      setSavedParams(existingParams);
      console.log('Parâmetro salvo com sucesso:', ast);
    } catch (e) {
      console.error('Erro ao salvar parâmetro:', e);
    }
  };

  const ASTtoEquationElements = (ast: any, variables: Variable[]): (string | Variable)[] => {
    if (!ast) return [];
    if (ast.type === 'CONSTANT') {
        if (typeof ast.value === 'string') return [`"${ast.value}"`];
        return [String(ast.value)];
    }
    if (ast.type === 'FIELD') {
      const v = variables.find(x => x.id === ast.value);
      return v ? [v] : [String(ast.value)];
    }
    if (ast.type === 'THIS_ROW') {
      const v = variables.find(x => x.id === ast.value);
      return ['THIS_ROW(', v ? v : String(ast.value), ')'];
    }
    if (ast.type === 'FUNCTION') {
      const els: (string | Variable)[] = [`${ast.value}(`];
      if (ast.args && ast.args.length > 0) {
        ast.args.forEach((arg: any, index: number) => {
          els.push(...ASTtoEquationElements(arg, variables));
          if (index < ast.args.length - 1) {
            els.push(';');
          }
        });
      }
      els.push(')');
      return els;
    }
    if (ast.type === 'OPERATION') {
      const ops: any = {
        ADD: '+', SUBTRACT: '-', MULTIPLY: '×', DIVIDE: '÷',
        EQUALS: '=', NOT_EQUALS: '<>', GREATER_THAN: '>', LESS_THAN: '<',
        GREATER_THAN_EQUALS: '>=', LESS_THAN_EQUALS: '<=', AND: 'AND', OR: 'OR'
      };
      return [
        '(',
        ...ASTtoEquationElements(ast.left, variables),
        ops[ast.operation] || '?',
        ...ASTtoEquationElements(ast.right, variables),
        ')'
      ];
    }
    return [];
  };

  const handleEditParam = (param: ContainerDef) => {
    setParamName(param.name);
    setReturnType(param.returnValue.type === 'CONSTANT' || param.returnValue.type === 'FIELD' ? 'number' : 'number');
    setFallback(param.fallbackValue?.toString() || '0');

    // Reconstruct equation elements from AST
    if (param.returnValue) {
        const reconstructedEls = ASTtoEquationElements(param.returnValue, ALL_VARIABLES);
        setEquationElements(reconstructedEls);
    } else {
        setEquationElements([]);
    }

    // The scope and targetForm we saved in _metadata
    if ((param as any)._metadata) {
      setTargetForm((param as any)._metadata.targetForm || 'global');
      setScope((param as any)._metadata.scope || 'individual');
    }
    setViewMode('editor');
  };

  const handleDeleteParam = (paramId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este parâmetro? Ele sumirá dos gráficos que o utilizam.')) return;
    const newParams = savedParams.filter(p => p.id !== paramId);
    setSavedParams(newParams);
    localStorage.setItem(PARAM_LOCAL_STORAGE_KEY, JSON.stringify(newParams));
  };

  const handleExportParams = () => {
    const exportData = {
      params: savedParams,
      globalAliases: globalAliases
    };
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "parametros_asa_branca.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImportParams = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const imported = JSON.parse(evt.target?.result as string);
        if (Array.isArray(imported)) {
          const merged = [...savedParams, ...imported.filter(imp => !savedParams.find(s => s.id === imp.id))];
          setSavedParams(merged);
          localStorage.setItem(PARAM_LOCAL_STORAGE_KEY, JSON.stringify(merged));
          alert('Parâmetros importados com sucesso!');
        } else if (imported && imported.params) {
          const mergedParams = [...savedParams, ...imported.params.filter((imp: any) => !savedParams.find(s => s.id === imp.id))];
          setSavedParams(mergedParams);
          localStorage.setItem(PARAM_LOCAL_STORAGE_KEY, JSON.stringify(mergedParams));
          
          if (imported.globalAliases && Array.isArray(imported.globalAliases)) {
            const mergedAliases = [...globalAliases, ...imported.globalAliases.filter((imp: any) => !globalAliases.find(a => a.id === imp.id))];
            setGlobalAliases(mergedAliases);
          }
          alert('Parâmetros e Agrupadores Globais importados com sucesso!');
        }
      } catch (err) {
        alert('Falha ao importar o arquivo JSON de parâmetros.');
      }
    };
    reader.readAsText(file);
  };

  const handleResetParams = () => {
    if (window.confirm('Isto apagará TODOS os parâmetros locais! Tem certeza?')) {
      setSavedParams([]);
      localStorage.removeItem(PARAM_LOCAL_STORAGE_KEY);
    }
  };


  const calculateSandbox = () => {
    try {
      const ast = generateASTPayload();
      
      const simulatedRow: Record<string, any> = {};
      let primaryFormId: string | undefined = undefined;

      Object.entries(sandboxRow).forEach(([key, value]) => {
         simulatedRow[key] = value; 
         if (key.includes('::')) {
             const [fId, fKey] = key.split('::');
             simulatedRow[fKey] = value;
             if (!primaryFormId && fId !== 'global') {
                 primaryFormId = fId;
             }
         }
      });
      
      if (primaryFormId) {
          simulatedRow['formId'] = primaryFormId;
      }

      // Emulate engine execution on single row
      const internalResult = processContainerForRow(ast, simulatedRow);
      // Fallback is terminal UI replacement
      const finalResult = internalResult === null ? ast.fallbackValue : internalResult;
      
      setSandboxResult({ internalResult, finalResult });
    } catch(err: any) {
      setSandboxResult({ internalResult: 'CRITICAL ERROR', finalResult: err.message });
    }
  };

  // Real-time auto-calculation hook
  React.useEffect(() => {
    if (liveTestMode) {
      calculateSandbox();
    }
  }, [liveTestMode, compiledAST, ruleRoot, equationElements, fallback, returnType, sandboxRow]);

  // MOCK DAG FOR TOPOLOGY TESTING
  const PARAM_CURRENT_ID = "param_custom_123";
  const DAG_MOCK_GRAPH: Record<string, string[]> = {
    'v8': [PARAM_CURRENT_ID], // Simulate that v8 (Custo/L) depends on this param creating a cyclic error!
  };

  // Field dependencies extractor
  const usedFieldIds = useMemo(() => {
    const deps = new Set<string>();
    
    // Scans AST recursively
    const scanRules = (group: RuleGroup) => {
      for (const child of group.children) {
        if (child.type === 'group') scanRules(child);
        else if (child.type === 'condition') deps.add(child.var.id);
      }
    };
    scanRules(ruleRoot);

    // Scans equation
    for (const el of equationElements) {
      if (typeof el === 'object') deps.add(el.id); // Add variable refs
    }

    return Array.from(deps);
  }, [ruleRoot, equationElements]);

  // Cyclomatic Dependency DAG Checker
  const hasCyclicDependency = useMemo(() => {
    // Traverses our mocked graph to check if any required dependency 
    // requires this param itself (looping)
    for (const depId of usedFieldIds) {
      if (DAG_MOCK_GRAPH[depId]?.includes(PARAM_CURRENT_ID)) {
        return true;
      }
    }
    return false;
  }, [usedFieldIds]);

  // Robust Math Syntax Linter
  const mathValidation = useMemo(() => {
    if (equationElements.length === 0) return { valid: true }; // empty is valid as literal fallback check
    let openParens = 0;
    let expectedType: 'Value' | 'Operator' | null = null;
    
    // First pass: token level checks and parenthesis balance
    for (let i = 0; i < equationElements.length; i++) {
      const el = equationElements[i];
      if (typeof el === 'object') {
        if (el.returnType === 'enum' || el.returnType === 'boolean') {
           // We might want to allow string fields inside FILTER condition (like [veiculo] = 'abc')
           // but for now, we keep it strict to numeric comparisons if variables are allowed.
           // However, if we allow categorical grouping, maybe we should relax this.
           // Relaxing for enum/text to allow FILTER comparisons
           // return { valid: false, error: "Variáveis de Texto/Boolean não podem ser usadas em equações matemáticas.", tokenIndex: i };
        }
      }
      if (typeof el === 'string') {
        if (el.endsWith('(')) openParens++;
        if (el === ')') {
          openParens--;
          if (openParens < 0) return { valid: false, error: "Parêntese fechando antecipadamente.", tokenIndex: i };
        }
      }
    }

    if (openParens > 0) {
      return { valid: false, error: `Falta fechar ${openParens} parêntese(s).`, tokenIndex: equationElements.length - 1 };
    }

    const getType = (el: any) => {
      if (typeof el === 'object') return 'V';
      if (!isNaN(Number(el))) return 'C';
      if (typeof el === 'string' && ((el.startsWith("'") && el.endsWith("'")) || (el.startsWith('"') && el.endsWith('"')) || (el.startsWith('`') && el.endsWith('`')))) return 'C';
      if (typeof el === 'string' && el.endsWith('(') && el.length > 1) return 'F';
      if (el === '(') return '(';
      if (el === ')') return ')';
      if (['+', '-', '×', '÷', '=', '<>', '>', '<', '>=', '<=', 'AND', 'OR'].includes(el)) return '+';
      if (el === ';') return ';';
      return '?';
    };

    // Second pass: adjacencies logic
    for (let i = 0; i < equationElements.length; i++) {
        const t1 = getType(equationElements[i]);
        const t2 = i + 1 < equationElements.length ? getType(equationElements[i+1]) : null;

        if (i === 0 && (t1 === '+' || t1 === ';')) return { valid: false, error: "Fórmula não pode iniciar com operador ou separador.", tokenIndex: i };
        if (i === equationElements.length - 1 && (t1 === '+' || t1 === 'F' || t1 === '(' || t1 === ';')) {
            return { valid: false, error: "Operador/Função sem operando à direita.", tokenIndex: i };
        }

        if (t2) {
            const pair = t1 + t2;
            if (pair === '++') return { valid: false, error: "Operador após outro operador.", tokenIndex: i + 1 };
            if (pair === '()' || pair === 'F)') return { valid: false, error: "Parênteses ou Função vazia.", tokenIndex: i + 1 };
            
            if (['VV', 'VC', 'CV', 'CC'].includes(pair)) return { valid: false, error: "Insira um operador matemático entre os valores.", tokenIndex: i + 1 };
            if (['V(', 'C(', 'VF', 'CF'].includes(pair)) return { valid: false, error: "Insira um operador antes do parêntese/função.", tokenIndex: i + 1 };
            if (pair === '+)' || pair === ';)') return { valid: false, error: "Operador ou separador antes de fechar parêntese.", tokenIndex: i };
            if (pair === ')V' || pair === ')C' || pair === ')F' || pair === ')(') return { valid: false, error: "Falta operador de cálculo após o parêntese.", tokenIndex: i + 1 };
            if (pair === '+;' || pair === ';+') return { valid: false, error: "Operador e separador não podem ficar juntos.", tokenIndex: i + 1 };
            if (pair === '(+' || pair === 'F+') return { valid: false, error: "Operador não pode ficar logo após abertura de parêntese ou função.", tokenIndex: i + 1 };
        }
    }
    
    return { valid: true };
  }, [equationElements]);

  const isMathValid = mathValidation.valid;


  // Health checks
  const orphansInUse = useMemo(() => {
    let orphans = 0;
    const countOrphansRec = (g: RuleGroup) => {
      g.children.forEach(c => {
        if (c.type === 'condition' && c.var.isOrphan) orphans++;
        else if (c.type === 'group') countOrphansRec(c);
      });
    };
    countOrphansRec(ruleRoot);
    equationElements.forEach(el => {
      if (typeof el !== 'string' && el.isOrphan) orphans++;
    });
    return orphans;
  }, [ruleRoot, equationElements]);

  const hasContent = ruleRoot.children.length > 0 || equationElements.length > 0;

  const handleDragStart = (e: React.DragEvent, variable: Variable) => {
    setDraggedVar(variable);
  };

  const handleScopeToggleClick = (newScope: "individual"|"geral") => {
    if (scope === newScope) return;
    if (hasContent) {
      setPendingScope(newScope);
    } else {
      setScope(newScope);
    }
  };

  const confirmScopeChange = () => {
    if (pendingScope) {
      setScope(pendingScope);
      // Clean canvas
      setRuleRoot({ type: 'group', id: 'root', operator: 'AND', children: [] });
      setEquationElements([]);
      setPendingScope(null);
    }
  };

  // Rules Logic Mutators
  const addConditionToGroup = (groupId: string, variable: Variable) => {
    const newRoot = { ...ruleRoot };
    const insertRec = (g: RuleGroup) => {
      if (g.id === groupId) {
        g.children.push({ type: 'condition', id: Date.now().toString(), var: variable, operator: 'eq', value: '' });
        return true;
      }
      for (const c of g.children) {
        if (c.type === 'group') {
          if (insertRec(c)) return true;
        }
      }
      return false;
    };
    insertRec(newRoot);
    setRuleRoot(newRoot);
  };

  const addGroupToGroup = (groupId: string, operator: 'AND'|'OR') => {
    const newRoot = { ...ruleRoot };
    const insertRec = (g: RuleGroup) => {
      if (g.id === groupId) {
        g.children.push({ type: 'group', id: Date.now().toString(), operator, children: [] });
        return true;
      }
      for (const c of g.children) {
        if (c.type === 'group') {
          if (insertRec(c)) return true;
        }
      }
      return false;
    };
    insertRec(newRoot);
    setRuleRoot(newRoot);
  };

  const removeRuleItem = (id: string) => {
    const newRoot = { ...ruleRoot };
    const removeRec = (g: RuleGroup) => {
      const idx = g.children.findIndex(x => x.id === id);
      if (idx > -1) {
        g.children.splice(idx, 1);
        return true;
      }
      for (const c of g.children) {
        if (c.type === 'group' && removeRec(c)) return true;
      }
      return false;
    };
    removeRec(newRoot);
    setRuleRoot(newRoot);
  };

  const setConditionProps = (id: string, operator: string, value: string) => {
    const newRoot = { ...ruleRoot };
    const setRec = (g: RuleGroup) => {
      const target = g.children.find(x => x.id === id);
      if (target && target.type === 'condition') {
        target.operator = operator;
        target.value = value;
        return true;
      }
      for (const c of g.children) {
        if (c.type === 'group' && setRec(c)) return true;
      }
      return false;
    };
    setRec(newRoot);
    setRuleRoot(newRoot);
  };
  
  const toggleGroupOp = (id: string) => {
    const newRoot = { ...ruleRoot };
    const twiddleRec = (g: RuleGroup) => {
      if (g.id === id) {
        g.operator = g.operator === 'AND' ? 'OR' : 'AND';
        return true;
      }
      for (const c of g.children) {
        if (c.type === 'group' && twiddleRec(c)) return true;
      }
      return false;
    };
    twiddleRec(newRoot);
    setRuleRoot(newRoot);
  };

  const getVarTypeColor = (type: VarType, isOrphan?: boolean) => {
    if (isOrphan) return 'bg-red-100 text-red-800 border-red-300 border-dashed';
    switch(type) {
      case 'number': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'enum': return 'bg-green-100 text-green-800 border-green-200';
      case 'text': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'meta': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'container': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100';
    }
  };

  const getVarTypeIcon = (type: VarType, isOrphan?: boolean) => {
    if (isOrphan) return <AlertCircle className="w-4 h-4 mr-1 text-red-600" />;
    switch(type) {
      case 'number': return <Hash className="w-4 h-4 mr-1" />;
      case 'enum': return <List className="w-4 h-4 mr-1" />;
      case 'text': return <AlignLeft className="w-4 h-4 mr-1" />;
      case 'meta': return <Database className="w-4 h-4 mr-1" />;
      case 'container': return <Box className="w-4 h-4 mr-1" />;
      default: return null;
    }
  };

  // Renderer recursivo dos grupos
  const renderRuleGroup = (group: RuleGroup, depth: number = 0) => {
    return (
      <div key={group.id} className={`rounded-xl border ${depth === 0 ? 'bg-white shadow-sm' : 'bg-slate-50 border-dashed ml-6 mt-4 relative'}`}>
        {depth > 0 && <div className="absolute -left-6 top-4 w-6 h-px bg-gray-300"></div>}
        
        <div className={`p-2 flex justify-between items-center ${depth === 0 ? (group.operator === 'AND' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white') : 'border-b bg-gray-100/50'} rounded-t-xl`}>
          <div className="flex items-center gap-2">
            <Button variant={depth === 0 ? 'secondary' : 'outline'} size="sm" onClick={() => toggleGroupOp(group.id)} className="h-7 text-xs font-bold w-16">
              {group.operator}
            </Button>
            {depth === 0 && <span className="font-medium text-sm">Raiz de Condições</span>}
            {depth > 0 && <span className="text-gray-500 text-xs">Sub-grupo</span>}
          </div>
          <div className="flex gap-2">
            {depth > 0 && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-500 hover:text-red-500" onClick={() => removeRuleItem(group.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {group.children.map((child, idx) => {
            if (child.type === 'group') return renderRuleGroup(child, depth + 1);
            
            // Condition block
            const c = child as RuleCondition;
            return (
              <div key={c.id} className="flex relative items-center gap-3 p-3 bg-white border rounded-lg shadow-sm hover:shadow transition-all group overflow-hidden">
                {/* Form Badge on the left block */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.var.formId === targetForm ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                <div className="text-xs font-bold text-gray-400 w-8 text-center ml-2">{idx > 0 || depth > 0 ? group.operator : 'SE'}</div>
                
                <div 
                  className={`flex flex-col rounded-md border min-w-[200px] max-w-[260px] cursor-help transition-colors ${getVarTypeColor(c.var.type, c.var.isOrphan)}`}
                  onClick={() => setInspectedVar(c.var)}
                >
                  <div className={`text-[9px] font-bold px-2 py-0.5 border-b uppercase tracking-wide
                    ${c.var.formId === targetForm ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20' : 'bg-rose-500/10 text-rose-700 border-rose-500/20'}`}>
                    {c.var.formName}
                  </div>
                  <div className="flex items-center p-1.5 px-2 text-sm font-medium">
                    {getVarTypeIcon(c.var.type, c.var.isOrphan)}
                    <span className="truncate">{c.var.name}</span>
                  </div>
                </div>

                <Select value={c.operator} onValueChange={(val) => setConditionProps(c.id, val, c.value)}>
                  <SelectTrigger className="w-[140px] h-10 shrink-0"><SelectValue placeholder="Operador" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eq">É Igual A</SelectItem>
                    <SelectItem value="neq">Diferente De</SelectItem>
                    {c.var.type === 'number' && <SelectItem value="gt">Maior Que</SelectItem>}
                    {c.var.type === 'number' && <SelectItem value="lt">Menor Que</SelectItem>}
                    {(c.var.type === 'enum' || c.var.type === 'text') && <SelectItem value="contains">Contém</SelectItem>}
                  </SelectContent>
                </Select>

                <Input 
                  placeholder="Valor Num ou Txt..." 
                  className="flex-1 h-10 min-w-[80px]"
                  value={c.value}
                  onChange={(e) => setConditionProps(c.id, c.operator, e.target.value)}
                />
                
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={() => removeRuleItem(c.id)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            );
          })}

          <div className="flex gap-2 pt-2">
            <div 
              className="flex-1 border-2 border-dashed border-blue-100 bg-blue-50/50 rounded-lg p-3 flex flex-col items-center justify-center text-blue-400 hover:bg-blue-50 transition-colors"
              onDragOver={e => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (draggedVar) addConditionToGroup(group.id, draggedVar); setDraggedVar(null); }}
            >
              <span className="text-sm">+ Arraste uma variável aqui</span>
            </div>
            <Button variant="outline" className="h-auto border-dashed text-gray-500" onClick={() => addGroupToGroup(group.id, 'AND')}>
              <FolderTree className="w-4 h-4 mr-2" /> Num Sub-grupo
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-[calc(100vh-140px)] w-full font-sans">
        <div className="flex items-center justify-between mb-4">
          <div className="flex bg-slate-200 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('editor')}
              className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${viewMode === 'editor' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Criar / Editar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Parâmetros Salvos ({savedParams.length})
            </button>
          </div>

          {viewMode === 'list' && (
            <div className="flex gap-2">
              <button 
                onClick={() => setAliasBuilderOpen(true)}
                className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold px-3 py-1.5 rounded-md hover:bg-blue-100 transition flex items-center"
              >
                <Layers size={14} className="mr-1" />
                Agrupadores Globais
              </button>
              <label className="cursor-pointer bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-md hover:bg-slate-700 transition flex items-center">
                <Upload size={14} className="mr-1" />
                Importar
                <input type="file" accept=".json" className="hidden" onChange={handleImportParams} />
              </label>
              <button onClick={handleExportParams} disabled={savedParams.length === 0} className="bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-md hover:bg-emerald-500 transition disabled:opacity-50 flex items-center">
                <Download size={14} className="mr-1" />
                Exportar JSON
              </button>
              <button onClick={handleResetParams} disabled={savedParams.length === 0} className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md hover:bg-red-500 transition disabled:opacity-50 flex items-center">
                <Trash2 size={14} className="mr-1" />
                Wipe Local
              </button>
            </div>
          )}
        </div>

        {viewMode === 'list' ? (
          <div className="flex-1 bg-white border rounded-xl shadow-sm p-6 overflow-y-auto">
            {savedParams.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <Database size={48} className="mb-4 opacity-20" />
                <p>Nenhum parâmetro customizado salvo localmente.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedParams.map(p => (
                  <div key={p.id} className="border border-slate-200 rounded-xl p-4 flex flex-col hover:border-blue-400 transition-colors bg-slate-50 group relative">
                    <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
                      <Box className="w-4 h-4 text-blue-500" />
                      {p.name}
                    </h3>
                    <div className="text-xs text-slate-500 mb-4 flex-1">
                      <div className="mb-2 p-1.5 bg-slate-200/50 rounded font-mono text-[10px] text-slate-600 break-words leading-relaxed border border-slate-200">
                        {p.returnValue?.type === 'CONSTANT' ? `Fixo: ${p.returnValue.value}` : parseCalculationToString(p.returnValue)}
                      </div>
                      ID: {p.id} <br/>
                      Retorno: <span className="font-mono bg-slate-200 px-1 rounded text-slate-700">{p.returnValue?.type || 'Não definido'}</span>
                    </div>

                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200 opacity-50 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEditParam(p)} className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center">
                        <Pencil size={12} className="mr-1" /> Editar
                      </button>
                      <button onClick={() => handleDeleteParam(p.id)} className="text-red-600 hover:text-red-800 text-xs font-bold flex items-center">
                        <Trash2 size={12} className="mr-1" /> Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-1 border rounded-xl overflow-hidden bg-slate-100 relative">
        {/* PANEL LEFT: VARIABLE LIBRARY */}
        <div className="w-80 bg-white border-r flex flex-col shrink-0 z-10 shadow-[2px_0_10px_rgba(0,0,0,0.02)]">
          <div className="p-4 border-b bg-slate-50 flex flex-col gap-3">
            <h3 className="font-bold text-gray-800 flex items-center">
              <Database className="w-5 h-5 mr-2 text-blue-600" />
              Biblioteca de Parametrização
            </h3>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <Input placeholder="Buscar variáveis..." className="pl-9 h-9" />
            </div>
            
            <div className="flex flex-col gap-1.5 mt-1 border border-slate-200 bg-white rounded-md p-2 shadow-sm">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Legenda de Permissões:</h4>
              <div className="flex items-center text-[11px] font-medium text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-100 border border-blue-300 mr-2"></span>
                <span>Aceita Operadores Matemáticos (+, -)</span>
              </div>
              <div className="flex items-center text-[11px] font-medium text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-100 border border-emerald-300 mr-2"></span>
                <span>Apenas Condicionais Lógicas (==)</span>
              </div>
              <div className="flex items-center text-[11px] font-medium text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-100 border border-amber-300 mr-2"></span>
                <span>Saída de Outro Cálculo (Container)</span>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-4 pb-20 p-4">
             {Array.from(new Set(ALL_VARIABLES.map(v => v.formName))).map(formName => {
                const varsInForm = ALL_VARIABLES.filter(v => v.formName === formName);
                if (!varsInForm.length) return null;
                
                return (
                  <div key={formName} className="space-y-2">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider sticky top-0 bg-white py-1 z-10">
                      {formName} <span className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] text-slate-500 font-bold">{varsInForm.length}</span>
                    </h4>
                    <div className="space-y-1.5">
                      {varsInForm.map(v => (
                        <div 
                          key={v.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, v)}
                          onClick={() => setInspectedVar(v)}
                          className={`flex items-center p-2 rounded-lg border text-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${getVarTypeColor(v.type, v.isOrphan)} ${v.isOrphan ? 'opacity-80' : ''}`}
                          title={v.desc}
                        >
                          {getVarTypeIcon(v.type, v.isOrphan)}
                          <span className="truncate font-medium flex-1">{v.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
             })}
          </div>
        </div>

        {/* CENTER CANVAS: BUILDER */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden relative flex flex-col pb-20">
          
          {/* Metadata Header */}
          <div className="m-6 bg-white p-6 rounded-xl border shadow-sm z-20 shrink-0">
            <div className="flex justify-between items-start gap-8">
              <div className="space-y-4 flex-1">
                <div className="space-y-1.5">
                  <Label className="text-gray-500 uppercase text-xs font-bold tracking-wider">Nome do Parâmetro</Label>
                  <Input className="text-lg font-bold h-12" value={paramName} onChange={e => setParamName(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-gray-500 text-xs font-bold uppercase">Formulário Fonte</Label>
                    <Select value={targetForm} onValueChange={setTargetForm}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="global">Todos (Global)</SelectItem>
                        {forms && forms.map(f => (
                           <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-500 text-xs font-bold uppercase">Tipo de Saída (Retorno)</Label>
                    <Select value={returnType} onValueChange={setReturnType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="number">Valor Numérico / Cálculo</SelectItem>
                        <SelectItem value="boolean">Booleano Lógico (Sim/Não)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-5 w-64 shrink-0 bg-slate-50 p-4 rounded-lg border">
                <div className="space-y-2">
                  <Label className="flex items-center justify-between text-xs font-bold uppercase text-gray-500">
                    <span>Motor de Escopo</span>
                    <Tooltip>
                      <TooltipTrigger><Info className="w-4 h-4 text-blue-400" /></TooltipTrigger>
                      <TooltipContent className="w-64">
                        <p><strong>Individual:</strong> Executado no celular, no momento exato do preenchimento da linha.</p>
                        <p className="mt-2"><strong>Geral:</strong> Varre banco de dados em chunks assíncronos no Dashboard.</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="flex items-center space-x-3 p-1.5 bg-white rounded-md border shadow-sm">
                    <span className={`text-xs pl-2 ${scope === 'individual' ? 'font-bold text-blue-700' : 'text-gray-400'}`}>Indiv.</span>
                    <Switch checked={scope === 'geral'} onCheckedChange={(e: any) => handleScopeToggleClick(scope === 'geral' ? 'individual' : 'geral')} />
                    <span className={`text-xs pr-2 ${scope === 'geral' ? 'font-bold text-indigo-700' : 'text-gray-400'}`}>Geral (Pipeline)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AND / OR BUILDER */}
          <div className="mx-6 mb-6 shrink-0">
            {renderRuleGroup(ruleRoot)}
          </div>

          {/* EQUATION BUILDER */}
          <div className={`mx-6 mb-12 shrink-0 bg-white border rounded-xl shadow-md overflow-hidden transition-all duration-500 ${!isMathValid ? 'border-red-400 ring-4 ring-red-100' : 'border-slate-200'}`}>
              <div className={`p-4 font-bold flex items-center justify-between text-white ${!isMathValid ? 'bg-red-600' : 'bg-slate-800'}`}>
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  <span className="text-lg">Fórmula de Retorno / Expressão</span>
                </div>
                {!isMathValid ? (
                  <Badge variant="destructive" className="bg-red-800 text-red-100 border-red-500"><AlertTriangle className="w-3 h-3 mr-2"/>Erro de Sintaxe</Badge>
                ) : (
                  <div className="flex items-center text-xs font-normal text-slate-300 gap-2 opacity-80 decoration-slate-400 cursor-help" title="Linhas vazias não distorcerão agregações como Médias.">
                    <Info className="w-4 h-4" /> Null-Safety Automático
                  </div>
                )}
              </div>
              
              <div className="p-2 border-b bg-slate-100/80 flex gap-2 flex-wrap items-center">
                <span className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wide">Constantes:</span>
                <div className="flex gap-1 items-center bg-white border rounded p-1">
                  <Input 
                    type="text"
                    placeholder='Ex: 50 ou "Carro"'
                    className="h-6 w-28 text-xs border-none shadow-none focus-visible:ring-0 text-center"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = e.currentTarget.value;
                        if (val) {
                          setEquationElements([...equationElements, val]);
                          e.currentTarget.value = "";
                        }
                      }
                    }}
                  />
                </div>
                <div className="w-px h-6 bg-slate-300 mx-2"></div>
                <span className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wide">Funções:</span>
                <Button variant="outline" className="bg-white" size="sm" onClick={() => setEquationElements([...equationElements, 'SUM('])}>SUM</Button>
                <Button variant="outline" className="bg-white" size="sm" onClick={() => setEquationElements([...equationElements, 'AVG('])}>AVG</Button>
                <Button variant="outline" className="bg-white" size="sm" onClick={() => setEquationElements([...equationElements, 'MAX('])}>MAX</Button>
                <Button variant="outline" className="bg-white" size="sm" onClick={() => setEquationElements([...equationElements, 'MIN('])}>MIN</Button>
                <Button variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200" size="sm" onClick={() => setEquationElements([...equationElements, 'FILTER('])}>FILTER</Button>
                <Button variant="outline" className="bg-purple-50 text-purple-700 border-purple-200" size="sm" onClick={() => setEquationElements([...equationElements, 'SWITCH('])}>SWITCH</Button>
                <Button variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" size="sm" onClick={() => setEquationElements([...equationElements, 'THIS_ROW('])}>THIS_ROW</Button>
                <Button variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" size="sm" onClick={() => setEquationElements([...equationElements, 'TIME('])}>TIME</Button>
                <Button variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" size="sm" onClick={() => setEquationElements([...equationElements, 'DATE('])}>DATE</Button>
                <Button variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200" size="sm" onClick={() => setEquationElements([...equationElements, 'HOURS_TO_TIME('])}>HOURS_TO_TIME</Button>
                <Button variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" size="sm" onClick={() => setEquationElements([...equationElements, 'PREVIOUS_RUNNING_SUM('])}>PREV_RUN_SUM</Button>
                <div className="w-px h-6 bg-slate-300 mx-2"></div>
                <span className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wide">Lógica:</span>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '='])}>=</Button>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '<>'])}>&lt;&gt;</Button>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '>'])}>&gt;</Button>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '<'])}>&lt;</Button>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '>='])}>&ge;</Button>
                <Button variant="outline" className="bg-white font-mono text-sm" size="sm" onClick={() => setEquationElements([...equationElements, '<='])}>&le;</Button>
                <Button variant="outline" className="bg-white font-mono text-xs" size="sm" onClick={() => setEquationElements([...equationElements, 'AND'])}>AND</Button>
                <Button variant="outline" className="bg-white font-mono text-xs" size="sm" onClick={() => setEquationElements([...equationElements, 'OR'])}>OR</Button>
              </div>

              <div className="flex gap-2 p-2 border-b bg-slate-100 overflow-x-auto items-center">
                <span className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wide">Matemática & Símbolos:</span>
                <Button variant="outline" className="bg-white font-mono text-lg" size="sm" onClick={() => setEquationElements([...equationElements, '+'])}>+</Button>
                <Button variant="outline" className="bg-white font-mono text-lg" size="sm" onClick={() => setEquationElements([...equationElements, '-'])}>-</Button>
                <Button variant="outline" className="bg-white font-mono text-lg" size="sm" onClick={() => setEquationElements([...equationElements, '×'])}>×</Button>
                <Button variant="outline" className="bg-white font-mono text-lg" size="sm" onClick={() => setEquationElements([...equationElements, '÷'])}>÷</Button>
                <Button variant="outline" className="bg-white font-mono font-bold" size="sm" onClick={() => setEquationElements([...equationElements, '('])}>( </Button>
                <Button variant="outline" className="bg-white font-mono font-bold" size="sm" onClick={() => setEquationElements([...equationElements, ')'])}> )</Button>
                <Button variant="outline" className="bg-slate-200 border-slate-300 font-mono font-bold text-lg" size="sm" onClick={() => setEquationElements([...equationElements, ';'])}>;</Button>
                
                <Button variant="ghost" className="ml-auto text-red-500 hover:text-red-700" size="sm" onClick={() => setEquationElements([])}>Limpar</Button>
              </div>
              
              <div 
                className={`p-6 min-h-[160px] flex flex-wrap content-start gap-2 ${!isMathValid ? 'bg-red-50' : 'bg-[#0f172a]'}`}
                onDragOver={e => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedEqIndex !== null) {
                    const newEls = [...equationElements];
                    const [moved] = newEls.splice(draggedEqIndex, 1);
                    newEls.push(moved);
                    setEquationElements(newEls);
                    setDraggedEqIndex(null);
                  } else if (draggedVar) {
                    setEquationElements([...equationElements, draggedVar]);
                  }
                  setDraggedVar(null);
                }}
              >
                {equationElements.map((el, i) => {
                  const isErrorToken = mathValidation.tokenIndex === i;
                  
                  if (typeof el === 'string') {
                    // String tokens (operators)
                    const isFunc = el.includes('(') && el.length > 1;
                    return (
                      <Tooltip key={i}>
                        <TooltipTrigger >
                          <div 
                            draggable
                            onDragStart={(e) => { e.stopPropagation(); setDraggedEqIndex(i); }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleEquationDrop(e, i)}
                            className="relative group cursor-grab active:cursor-grabbing"
                            onClick={() => setEquationElements(equationElements.filter((_, idx) => idx !== i))}
                            title="Clique para remover, arraste para trocar de lado"
                          >
                            <span className={`font-mono text-xl py-1 px-2 rounded-md flex items-center transition-all hover:bg-white/10 ${isErrorToken ? 'text-white bg-red-600 ring-2 ring-red-400 ring-offset-2 ring-offset-slate-900 animate-pulse' : (!isMathValid ? 'text-red-900/40' : (isFunc ? 'text-indigo-400 font-bold' : 'text-blue-300 font-bold'))} `}>
                              {el}
                            </span>
                            <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white rounded-full p-0.5 shadow-md z-10 hidden md:block">
                               <X className="w-3 h-3" />
                            </div>
                          </div>
                        </TooltipTrigger>
                        {isErrorToken && (
                          <TooltipContent className="bg-red-600 text-white border-red-800 font-bold">
                            🔍 ERRO DETECTADO AQUI
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  }
                  // Variable pill with Form Context
                  return (
                    <Tooltip key={i}>
                      <TooltipTrigger >
                        <div 
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); setDraggedEqIndex(i); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => handleEquationDrop(e, i)}
                          className={`flex flex-col rounded-lg border shadow-sm transition-all overflow-hidden relative group cursor-grab active:cursor-grabbing
                          ${isErrorToken ? 'bg-red-500 border-red-400 text-white ring-2 ring-red-400 ring-offset-2 ring-offset-slate-900 animate-pulse' : (!isMathValid ? 'bg-red-200/50 border-red-300/50 text-red-900/50' : 'bg-slate-700 border-slate-600 text-white')}`}
                        >
                          <div className={`text-[9px] font-bold px-2 py-0.5 uppercase tracking-wide
                            ${isErrorToken ? 'bg-black/20 text-white' : (el.formId === targetForm ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300')}`}>
                            {el.formName}
                          </div>
                          <div className="flex items-center px-3 py-1.5 text-sm font-medium">
                            <Hash className={`w-3 h-3 mr-1.5 ${isErrorToken ? 'opacity-100' : 'opacity-60'}`} />
                            {el.name}
                            <button className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 hover:bg-red-500 rounded-full p-0.5 absolute right-1 top-1 z-10" onClick={(e) => { e.stopPropagation(); setEquationElements(equationElements.filter((_, idx) => idx !== i)); }}>
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        </div>
                      </TooltipTrigger>
                      {isErrorToken && (
                          <TooltipContent className="bg-red-600 text-white border-red-800 font-bold">
                            🔍 TIPO INCOMPATÍVEL
                          </TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
                {equationElements.length > 0 && isMathValid && (() => {
                  let open = 0;
                  equationElements.forEach(e => {
                    if (typeof e === 'string') {
                      if (e.includes('(')) open++;
                      if (e === ')') open--;
                    }
                  });
                  return open > 0 ? (
                    <span className="font-mono text-xl py-1 px-1 flex items-center text-slate-500/50 italic animate-pulse">
                      {')'.repeat(open)} <span className="text-xs ml-2 font-sans tracking-tight">fechamento implícito...</span>
                    </span>
                  ) : null;
                })()}

                {equationElements.length === 0 && (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 mt-4 font-medium">
                    <Calculator className="w-8 h-8 mb-3 opacity-20" />
                    <p>Construa a equação clicando nos botões ou arrastando variáveis numéricas aqui.</p>
                    <p className="text-xs font-normal mt-2 opacity-50">Variáveis de texto podem ser arrastadas para uso com a função FILTER().</p>
                  </div>
                )}
              </div>
              {!isMathValid && equationElements.length > 0 && (
                <div className="bg-red-100 text-red-800 text-sm p-3 font-medium flex items-center px-6 border-t border-red-200">
                  <AlertCircle className="w-5 h-5 mr-3 text-red-600 shrink-0" />
                  <div>
                    <strong className="block text-red-900 mb-0.5">Erro de Sintaxe (Índice {mathValidation.tokenIndex})</strong>
                    {mathValidation.error || "Verifique se não há operadores duplicados ou parênteses vazios."}
                  </div>
                </div>
              )}
            </div>

        </div>

        {/* PANEL RIGHT: INSPECTOR & VALIDATOR */}
        <div className="w-80 bg-white border-l flex flex-col shrink-0 z-20 shadow-[-2px_0_10px_rgba(0,0,0,0.02)]">
          <div className="p-4 border-b bg-slate-50">
            <h3 className="font-bold text-gray-800 flex items-center">
              <ShieldCheck className="w-5 h-5 mr-2 text-green-600" />
              Inspetor e DAG
            </h3>
          </div>
          
          <div className="p-5 flex-1 overflow-y-auto space-y-8">
            
            {/* Health Validator */}
            <div className="space-y-3">
              <Label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Saúde do Parâmetro (DAG)</Label>

              {hasCyclicDependency ? (
                <div className="p-4 rounded-xl border bg-red-50 border-red-300 shadow-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-bold text-red-800 text-sm">Loop Infinito Lógico (DAG)</h4>
                      <p className="text-xs text-red-700 mt-1.5 leading-snug font-medium">Você introduziu uma variável (Container) que depende ciclicamente deste que você está editando. Salvar resultará em travamento de Heap do Navegador (OOM).</p>
                    </div>
                  </div>
                </div>
              ) : orphansInUse > 0 ? (
                 <div className="p-4 rounded-xl border bg-amber-50 border-amber-300">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm">Estado Órfão Iminente</h4>
                      <p className="text-xs text-amber-700 mt-1.5 leading-snug font-medium">Foram detectadas {orphansInUse} referências a variáveis que não existem mais no formulário. Salve sob risco matemático.</p>
                    </div>
                  </div>
               </div>
              ) : isMathValid ? (
                <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-100 p-1.5 rounded-full shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-emerald-800 text-sm">Estrutura Estável</h4>
                      <p className="text-[11px] text-emerald-700 mt-0.5 leading-tight">Sem loops lógicos detectados.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm">Erro de Equação</h4>
                      <p className="text-[11px] text-amber-700 mt-0.5 leading-tight">Corrija a sintaxe de saída para compilar.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Variable Inspection */}
            <div className="space-y-4 pt-6 border-t border-slate-100 transition-all">
              <Label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                <span>Inspeção Focada</span>
                {inspectedVar && <Badge variant="outline" className="text-[9px] h-4 leading-none">VAR</Badge>}
              </Label>
              
              {inspectedVar ? (
                <div className={`p-4 rounded-xl border shadow-sm relative overflow-hidden ${getVarTypeColor(inspectedVar.type)}`}>
                  <div className="absolute right-0 top-0 opacity-10 scale-[300%] rotate-12 translate-x-4">
                    {getVarTypeIcon(inspectedVar.type)}
                  </div>
                  <div className="relative z-10">
                    <h5 className="font-bold text-base mb-1 truncate">{inspectedVar.name}</h5>
                    <Badge variant="outline" className="bg-white/50 mb-3 border-current">Type: {inspectedVar.type}</Badge>
                    
                    <p className="text-xs opacity-90 leading-relaxed font-medium mb-4">
                      {inspectedVar.desc || "Variável extraída do form original."}
                    </p>

                    {inspectedVar.isOrphan && (
                      <div className="bg-red-50 text-red-700 border border-red-200 p-2 rounded text-xs font-bold flex items-center mb-4">
                        <AlertCircle className="w-3 h-3 mr-1.5" /> ESTADO ÓRFÃO
                      </div>
                    )}
                    
                    {inspectedVar.returnType && (
                      <div className="bg-black/5 p-2 rounded text-xs font-mono font-medium">
                        Saída: {inspectedVar.returnType}
                      </div>
                    )}

                    <Button size="sm" variant="ghost" className="w-full mt-4 bg-white/40 hover:bg-white/60" onClick={() => setInspectedVar(null)}>
                      Desfazer Foco
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 text-center flex flex-col items-center">
                  <div className="bg-white p-3 rounded-full shadow-sm mb-3 text-slate-400">
                    <Search className="w-5 h-5" />
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Clique em qualquer pílula no canvas ou na biblioteca para focar suas origens e tipagem.</p>
                </div>
              )}
            </div>

            {/* Global Container Properties */}
            <div className="space-y-4 pt-6 border-t border-slate-100">
              <Label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Proteção Contra Craches (RN11)</Label>
              
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-bold text-slate-700">Fallback Visível / Terminal</Label>
                    <Tooltip>
                      <TooltipTrigger><Info className="w-4 h-4 text-blue-400" /></TooltipTrigger>
                      <TooltipContent side="left" className="w-64 p-3 leading-relaxed">
                        Este valor só é exibido na <b>tela do usuário final</b> se e somente se as equações internas colapsarem por falta de dados (Nulos). <br/><br/>
                        <i>Ele não polui a média ou a matemática de banco. (Paradoxo Null-Safety)</i>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input 
                    placeholder="Ex: 0" 
                    value={fallback} onChange={e => setFallback(e.target.value)}
                    className="bg-white border-slate-300 font-mono text-lg font-bold"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 gap-2 flex flex-col">
               <Button variant="ghost" className="w-full text-blue-600 text-xs flex justify-between h-10 px-4 bg-blue-50 hover:bg-blue-100 rounded-lg">
                  <span className="flex items-center"><History className="w-4 h-4 mr-2" /> Histórico de AST (JSON)</span>
                  <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-[10px] font-bold">V1.2</span>
                </Button>
                
                <Button 
                variant="outline" 
                className={`w-full text-xs flex justify-center h-10 px-4 mt-2 border rounded-lg transition-colors ${liveTestMode ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`}
                disabled={!isMathValid || hasCyclicDependency || orphansInUse > 0}
                onClick={() => {
                   setSandboxResult(null);
                   setSandboxRow({});
                   setShowSandbox(true);
                }}
               >
                  {liveTestMode ? <Play className="w-4 h-4 mr-2" /> : <Calculator className="w-4 h-4 mr-2" />} 
                  {liveTestMode ? 'Test Runner Ativo (Ver Sandbox)' : 'Simulador Engine (Sandbox)'}
               </Button>
            </div>
          </div>
          
          <div className="p-5 border-t bg-slate-50 shadow-[0_-5px_15px_rgba(0,0,0,0.02)] z-30">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200 h-12 text-sm font-bold rounded-xl" 
                    disabled={!isMathValid || orphansInUse > 0 || hasCyclicDependency}
                    onClick={handleSaveAndCompile}>
              Salvar Versão Atual e Compilar
            </Button>
            {(orphansInUse > 0 || !isMathValid || hasCyclicDependency) && (
              <p className="text-[10px] text-center text-red-500 font-bold mt-3">Correções requeridas antes de salvar.</p>
            )}
          </div>
        </div>

      {/* Sandbox Simulator Dialog */}
      <Dialog open={showSandbox} onOpenChange={(o) => {
          setShowSandbox(o);
          if(!o) setLiveTestMode(false);
      }}>
        <DialogContent className="max-w-3xl bottom-4 top-auto translate-y-0" style={{ pointerEvents: liveTestMode ? 'none' : 'auto' }}>
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden" style={{ pointerEvents: 'auto' }}>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle className="flex items-center gap-2 text-indigo-700">
                 <Calculator className="w-5 h-5"/> Simulador Engine de Teste
              </DialogTitle>
              <DialogDescription>
                Insira valores simulados para as variáveis dependentes desta árvore e verifique o resultado do Motor de Cálculo antes de publicar.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-4 p-6">
              <div className="flex-1 space-y-3 bg-slate-50 p-4 border rounded-xl relative">
              
              <div className="absolute top-4 right-4 flex items-center bg-indigo-100 p-1 rounded-md border border-indigo-200">
                <Switch size="sm" checked={liveTestMode} onCheckedChange={setLiveTestMode} />
                <span className="text-[10px] font-bold text-indigo-700 ml-2 uppercase">Tempo Real</span>
              </div>
              
              <h4 className="font-bold text-xs uppercase text-slate-500">Massa de Dados (Input)</h4>
              {usedFieldIds.length === 0 ? (
                <p className="text-xs text-slate-400">Nenhuma variável dinâmica detectada (Cálculo Fixo).</p>
              ) : (
                usedFieldIds.map(fid => {
                  const v = ALL_VARIABLES.find(x => x.id === fid);
                  return (
                    <div key={fid} className="flex flex-col gap-1">
                      <Label className="text-xs text-slate-700">{v?.name || fid}</Label>
                      <Input 
                        placeholder={v?.type === 'number' ? 'Número' : 'Texto'} 
                        value={sandboxRow[fid] || ''}
                        onChange={e => setSandboxRow({ ...sandboxRow, [fid]: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>
                  );
                })
              )}
              {!liveTestMode && (
                <Button className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={calculateSandbox}>
                  Processar Engine
                </Button>
              )}
              {liveTestMode && (
                <div className="w-full mt-4 bg-indigo-100/50 text-indigo-500 font-bold text-xs p-2 text-center rounded">
                  Calculando em tempo real... Mova os blocos no Canvas atrás
                </div>
              )}
            </div>
            
            <div className="flex-1 space-y-4">
              {sandboxResult ? (
                 <>
                   <Card className="p-4 bg-white border shadow-sm">
                     <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Processamento Interno</h4>
                     {sandboxResult.internalResult === null ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded text-sm font-medium border border-amber-200">
                          <AlertCircle className="w-4 h-4"/> NULL / Abortado (Null-Safety)
                        </div>
                     ) : (
                        <div className="text-2xl font-mono text-slate-800 break-all">
                          {String(sandboxResult.internalResult)}
                        </div>
                     )}
                     <p className="text-[10px] text-slate-400 mt-2">Valor matemático usado dentro do banco de dados e agregações em lote.</p>
                   </Card>
                   
                   <Card className="p-4 bg-slate-900 border shadow-sm text-white relative overflow-hidden">
                     <div className="absolute right-0 bottom-0 opacity-10">
                        <MoveRight className="w-24 h-24" />
                     </div>
                     <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-2 relative z-10">Interface do Usuário (Final)</h4>
                     <div className="text-3xl font-black text-emerald-400 relative z-10 break-all">
                       {String(sandboxResult.finalResult)}
                     </div>
                     <p className="text-[10px] text-slate-400 mt-2 relative z-10">
                       Se interno = NULL, este é o Fallback configurado. Caso contrário, é o cálculo bruto.
                     </p>
                   </Card>
                 </>
              ) : (
                <div className="h-full flex items-center justify-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50 p-6 text-center text-sm text-slate-400">
                  Preencha os valores e clique em Processar para ver como os operadores do Engine lidariam com o Paradoxo do Vazio e Divs. por Zero.
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 bg-slate-50 border-t pt-4">
            <Button variant="outline" onClick={() => setShowSandbox(false)}>Fechar Simulador</Button>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scope Toggling Alert Dialog */}
      <Dialog open={pendingScope !== null} onOpenChange={(o) => { if(!o) setPendingScope(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Alteração de Motor de Escopo (RN07)</DialogTitle>
            <DialogDescription className="pt-3 text-base text-slate-600 leading-relaxed">
              Ao alternar de <strong>{scope === 'individual' ? 'Individual' : 'Geral'}</strong> para <strong>{pendingScope === 'individual' ? 'Individual' : 'Geral'}</strong>, os comportamentos de intra-agregação e pipeline assíncrono diferem drasticamente na arquitetura.
              <br/><br/>
              Como você já construiu lógicas no canvas atual, <strong>toda sua árvore de design será resetada</strong> para evitar transpilamentos de AST corrompidos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPendingScope(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmScopeChange}>Estou Ciente, Resetar Canvas e Mudar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Salvar Versionamento e Mostrar Transpilado AST */}
      <Dialog open={showASTDialog} onOpenChange={setShowASTDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
               Compilador AST (JSON Preview)
            </DialogTitle>
            <DialogDescription>
              Este é o Payload Hierárquico que será salvo no banco e parseado pelo Engine Virtual.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-900 rounded-lg p-4 overflow-auto text-xs font-mono text-emerald-400 mt-2 flex-grow shadow-inner">
            <pre className="whitespace-pre-wrap">{JSON.stringify(compiledAST, null, 2)}</pre>
          </div>
          <DialogFooter className="mt-4 shrink-0">
            <Button onClick={() => setShowASTDialog(false)}>Fechar Validador AST</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
          </div>
        )}
      </div>

      <AliasBuilder 
         open={aliasBuilderOpen} 
         onClose={() => setAliasBuilderOpen(false)} 
         globalAliases={globalAliases} 
         setGlobalAliases={setGlobalAliases} 
         allFields={ALL_VARIABLES.filter(v => v.formId !== 'global')} 
      />
    </TooltipProvider>
  );
}
